/**
 * Advanced Permission System Configuration
 * Combines RBAC with ABAC for fine-grained access control
 */

// Action types that can be performed on resources
export const ACTIONS = {
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  APPROVE: 'approve',
  EXPORT: 'export',
  IMPORT: 'import',
  SHARE: 'share',
  ARCHIVE: 'archive',
  RESTORE: 'restore',
};

// Scope levels for data access
export const SCOPES = {
  OWN: 'own',           // Only user's own data
  TEAM: 'team',         // User's team data
  DEPARTMENT: 'dept',   // User's department data
  LOCATION: 'location', // User's location data
  ALL: 'all',          // All data in system
};

// Field-level access for sensitive data
export const FIELD_LEVELS = {
  BASIC: 'basic',
  DETAILED: 'detailed',
  SENSITIVE: 'sensitive',
  FINANCIAL: 'financial',
  PERSONAL: 'personal',
};

// Module and resource definitions
export const PERMISSION_MODULES = {
  dashboard: {
    name: 'Dashboard',
    icon: 'IconDashboard',
    description: 'Analytics and reporting dashboards',
    resources: {
      overview: {
        name: 'Overview Dashboard',
        actions: [ACTIONS.VIEW, ACTIONS.EXPORT],
        scopes: [SCOPES.OWN, SCOPES.TEAM, SCOPES.DEPARTMENT, SCOPES.ALL],
        description: 'Main dashboard with KPIs and metrics',
      },
      analytics: {
        name: 'Analytics',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.EXPORT],
        scopes: [SCOPES.OWN, SCOPES.TEAM, SCOPES.DEPARTMENT, SCOPES.ALL],
        description: 'Detailed analytics and custom reports',
      },
      reports: {
        name: 'Reports',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE, ACTIONS.EXPORT, ACTIONS.SHARE],
        scopes: [SCOPES.OWN, SCOPES.TEAM, SCOPES.DEPARTMENT, SCOPES.ALL],
        fields: [FIELD_LEVELS.BASIC, FIELD_LEVELS.DETAILED, FIELD_LEVELS.FINANCIAL],
        description: 'Custom report generation and management',
      },
    },
  },
  
  staff: {
    name: 'Staff Management',
    icon: 'IconUsers',
    description: 'Employee and user management',
    resources: {
      profiles: {
        name: 'Staff Profiles',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE, ACTIONS.EXPORT],
        scopes: [SCOPES.OWN, SCOPES.TEAM, SCOPES.DEPARTMENT, SCOPES.ALL],
        fields: [FIELD_LEVELS.BASIC, FIELD_LEVELS.DETAILED, FIELD_LEVELS.PERSONAL, FIELD_LEVELS.FINANCIAL],
        description: 'View and manage staff profiles',
      },
      permissions: {
        name: 'Permissions',
        actions: [ACTIONS.VIEW, ACTIONS.EDIT, ACTIONS.APPROVE],
        scopes: [SCOPES.TEAM, SCOPES.DEPARTMENT, SCOPES.ALL],
        description: 'Manage user permissions and roles',
      },
      departments: {
        name: 'Departments',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE],
        scopes: [SCOPES.DEPARTMENT, SCOPES.ALL],
        description: 'Department structure management',
      },
      roles: {
        name: 'Roles',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE],
        scopes: [SCOPES.ALL],
        description: 'Role and permission template management',
      },
    },
  },
  
  scheduling: {
    name: 'Scheduling',
    icon: 'IconCalendar',
    description: 'Schedule and resource management',
    resources: {
      schedules: {
        name: 'Schedules',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE, ACTIONS.APPROVE],
        scopes: [SCOPES.OWN, SCOPES.TEAM, SCOPES.DEPARTMENT, SCOPES.ALL],
        description: 'View and manage schedules',
      },
      availability: {
        name: 'Availability',
        actions: [ACTIONS.VIEW, ACTIONS.EDIT],
        scopes: [SCOPES.OWN, SCOPES.TEAM, SCOPES.ALL],
        description: 'Manage resource availability',
      },
      templates: {
        name: 'Schedule Templates',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE, ACTIONS.SHARE],
        scopes: [SCOPES.TEAM, SCOPES.DEPARTMENT, SCOPES.ALL],
        description: 'Schedule template management',
      },
    },
  },
  
  customers: {
    name: 'Customers',
    icon: 'IconUserCircle',
    description: 'Customer relationship management',
    resources: {
      profiles: {
        name: 'Customer Profiles',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE, ACTIONS.EXPORT],
        scopes: [SCOPES.OWN, SCOPES.TEAM, SCOPES.LOCATION, SCOPES.ALL],
        fields: [FIELD_LEVELS.BASIC, FIELD_LEVELS.DETAILED, FIELD_LEVELS.FINANCIAL, FIELD_LEVELS.SENSITIVE],
        description: 'Customer information and history',
      },
      communications: {
        name: 'Communications',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE],
        scopes: [SCOPES.OWN, SCOPES.TEAM, SCOPES.ALL],
        description: 'Customer communication history',
      },
      documents: {
        name: 'Documents',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE, ACTIONS.SHARE],
        scopes: [SCOPES.OWN, SCOPES.TEAM, SCOPES.ALL],
        description: 'Customer document management',
      },
    },
  },
  
  finance: {
    name: 'Finance',
    icon: 'IconCurrencyDollar',
    description: 'Financial management and reporting',
    resources: {
      transactions: {
        name: 'Transactions',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE, ACTIONS.APPROVE, ACTIONS.EXPORT],
        scopes: [SCOPES.OWN, SCOPES.DEPARTMENT, SCOPES.ALL],
        fields: [FIELD_LEVELS.BASIC, FIELD_LEVELS.DETAILED, FIELD_LEVELS.FINANCIAL],
        description: 'Financial transactions',
      },
      invoices: {
        name: 'Invoices',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE, ACTIONS.APPROVE],
        scopes: [SCOPES.OWN, SCOPES.TEAM, SCOPES.ALL],
        description: 'Invoice management',
      },
      reports: {
        name: 'Financial Reports',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EXPORT],
        scopes: [SCOPES.DEPARTMENT, SCOPES.ALL],
        description: 'Financial reporting and analysis',
      },
    },
  },
  
  operations: {
    name: 'Operations',
    icon: 'IconSettings',
    description: 'Operational management',
    resources: {
      vehicles: {
        name: 'Vehicles',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE, ACTIONS.APPROVE],
        scopes: [SCOPES.LOCATION, SCOPES.ALL],
        description: 'Vehicle fleet management',
      },
      maintenance: {
        name: 'Maintenance',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE, ACTIONS.APPROVE],
        scopes: [SCOPES.LOCATION, SCOPES.ALL],
        description: 'Maintenance scheduling and tracking',
      },
      inventory: {
        name: 'Inventory',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE],
        scopes: [SCOPES.LOCATION, SCOPES.ALL],
        description: 'Inventory management',
      },
    },
  },
  
  system: {
    name: 'System',
    icon: 'IconServer',
    description: 'System administration',
    resources: {
      settings: {
        name: 'System Settings',
        actions: [ACTIONS.VIEW, ACTIONS.EDIT],
        scopes: [SCOPES.ALL],
        description: 'System configuration',
      },
      integrations: {
        name: 'Integrations',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE],
        scopes: [SCOPES.ALL],
        description: 'Third-party integrations',
      },
      audit: {
        name: 'Audit Logs',
        actions: [ACTIONS.VIEW, ACTIONS.EXPORT],
        scopes: [SCOPES.ALL],
        description: 'System audit trail',
      },
      backups: {
        name: 'Backups',
        actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.RESTORE],
        scopes: [SCOPES.ALL],
        description: 'Data backup management',
      },
    },
  },
};

// Permission groups (templates)
export const PERMISSION_GROUPS = {
  'basic-user': {
    name: 'Basic User',
    description: 'Standard user with limited access to own data',
    color: 'blue',
    permissions: {
      'dashboard.overview.view': { scope: SCOPES.OWN },
      'staff.profiles.view': { scope: SCOPES.OWN, fields: [FIELD_LEVELS.BASIC] },
      'scheduling.schedules.view': { scope: SCOPES.OWN },
      'scheduling.availability.view': { scope: SCOPES.OWN },
      'scheduling.availability.edit': { scope: SCOPES.OWN },
      'customers.profiles.view': { scope: SCOPES.OWN, fields: [FIELD_LEVELS.BASIC] },
    },
  },
  
  'team-member': {
    name: 'Team Member',
    description: 'Regular team member with team-wide access',
    color: 'green',
    inherits: ['basic-user'],
    permissions: {
      'dashboard.overview.view': { scope: SCOPES.TEAM },
      'dashboard.analytics.view': { scope: SCOPES.TEAM },
      'staff.profiles.view': { scope: SCOPES.TEAM, fields: [FIELD_LEVELS.BASIC, FIELD_LEVELS.DETAILED] },
      'scheduling.schedules.view': { scope: SCOPES.TEAM },
      'scheduling.schedules.create': { scope: SCOPES.TEAM },
      'scheduling.schedules.edit': { scope: SCOPES.OWN },
      'customers.profiles.view': { scope: SCOPES.TEAM, fields: [FIELD_LEVELS.BASIC, FIELD_LEVELS.DETAILED] },
      'customers.communications.view': { scope: SCOPES.OWN },
      'customers.communications.create': { scope: SCOPES.OWN },
    },
  },
  
  'team-lead': {
    name: 'Team Lead',
    description: 'Team leader with management capabilities',
    color: 'indigo',
    inherits: ['team-member'],
    permissions: {
      'dashboard.reports.view': { scope: SCOPES.TEAM },
      'dashboard.reports.create': { scope: SCOPES.TEAM },
      'staff.profiles.edit': { scope: SCOPES.TEAM, fields: [FIELD_LEVELS.BASIC, FIELD_LEVELS.DETAILED] },
      'staff.permissions.view': { scope: SCOPES.TEAM },
      'scheduling.schedules.edit': { scope: SCOPES.TEAM },
      'scheduling.schedules.approve': { scope: SCOPES.TEAM },
      'scheduling.templates.view': { scope: SCOPES.TEAM },
      'scheduling.templates.create': { scope: SCOPES.TEAM },
      'customers.profiles.edit': { scope: SCOPES.TEAM, fields: [FIELD_LEVELS.BASIC, FIELD_LEVELS.DETAILED] },
    },
  },
  
  'department-manager': {
    name: 'Department Manager',
    description: 'Department manager with broad access',
    color: 'purple',
    inherits: ['team-lead'],
    permissions: {
      'dashboard.overview.view': { scope: SCOPES.DEPARTMENT },
      'dashboard.analytics.view': { scope: SCOPES.DEPARTMENT },
      'dashboard.reports.view': { scope: SCOPES.DEPARTMENT },
      'dashboard.reports.export': { scope: SCOPES.DEPARTMENT },
      'staff.profiles.view': { scope: SCOPES.DEPARTMENT, fields: [FIELD_LEVELS.BASIC, FIELD_LEVELS.DETAILED, FIELD_LEVELS.PERSONAL] },
      'staff.profiles.create': { scope: SCOPES.DEPARTMENT },
      'staff.profiles.edit': { scope: SCOPES.DEPARTMENT },
      'staff.permissions.edit': { scope: SCOPES.DEPARTMENT, excludeRoles: ['admin', 'department-manager'] },
      'staff.departments.view': { scope: SCOPES.ALL },
      'scheduling.schedules.delete': { scope: SCOPES.DEPARTMENT },
      'customers.profiles.view': { scope: SCOPES.DEPARTMENT, fields: [FIELD_LEVELS.BASIC, FIELD_LEVELS.DETAILED, FIELD_LEVELS.FINANCIAL] },
      'finance.transactions.view': { scope: SCOPES.DEPARTMENT, fields: [FIELD_LEVELS.BASIC] },
      'finance.invoices.view': { scope: SCOPES.DEPARTMENT },
    },
  },
  
  'finance-user': {
    name: 'Finance User',
    description: 'Finance team member with financial access',
    color: 'yellow',
    permissions: {
      'dashboard.overview.view': { scope: SCOPES.ALL },
      'dashboard.reports.view': { scope: SCOPES.ALL, filter: 'financial' },
      'customers.profiles.view': { scope: SCOPES.ALL, fields: [FIELD_LEVELS.BASIC, FIELD_LEVELS.FINANCIAL] },
      'finance.transactions.view': { scope: SCOPES.ALL },
      'finance.transactions.create': { scope: SCOPES.ALL },
      'finance.transactions.edit': { scope: SCOPES.ALL },
      'finance.invoices.view': { scope: SCOPES.ALL },
      'finance.invoices.create': { scope: SCOPES.ALL },
      'finance.invoices.edit': { scope: SCOPES.ALL },
      'finance.reports.view': { scope: SCOPES.ALL },
      'finance.reports.create': { scope: SCOPES.ALL },
      'finance.reports.export': { scope: SCOPES.ALL },
    },
  },
  
  'operations-manager': {
    name: 'Operations Manager',
    description: 'Operations management access',
    color: 'orange',
    permissions: {
      'dashboard.overview.view': { scope: SCOPES.ALL },
      'dashboard.analytics.view': { scope: SCOPES.ALL, filter: 'operations' },
      'scheduling.schedules.view': { scope: SCOPES.ALL },
      'scheduling.schedules.create': { scope: SCOPES.ALL },
      'scheduling.schedules.edit': { scope: SCOPES.ALL },
      'scheduling.schedules.approve': { scope: SCOPES.ALL },
      'operations.vehicles.view': { scope: SCOPES.ALL },
      'operations.vehicles.create': { scope: SCOPES.ALL },
      'operations.vehicles.edit': { scope: SCOPES.ALL },
      'operations.maintenance.view': { scope: SCOPES.ALL },
      'operations.maintenance.create': { scope: SCOPES.ALL },
      'operations.maintenance.approve': { scope: SCOPES.ALL },
      'operations.inventory.view': { scope: SCOPES.ALL },
      'operations.inventory.edit': { scope: SCOPES.ALL },
    },
  },
  
  'system-admin': {
    name: 'System Administrator',
    description: 'Full system access excluding financial',
    color: 'red',
    permissions: {
      // All permissions except sensitive financial
      '*.*.*': { scope: SCOPES.ALL },
    },
    denials: [
      'finance.transactions.delete',
      'finance.reports.delete',
    ],
  },
};

// Special conditions that can be applied to permissions
export const PERMISSION_CONDITIONS = {
  timeRestriction: {
    name: 'Time Restriction',
    description: 'Limit access to specific times',
    fields: {
      startTime: { type: 'time', label: 'Start Time' },
      endTime: { type: 'time', label: 'End Time' },
      timezone: { type: 'select', label: 'Timezone', options: ['UTC', 'Europe/London', 'America/New_York'] },
      daysOfWeek: { type: 'multiselect', label: 'Days', options: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
    },
  },
  
  locationRestriction: {
    name: 'Location Restriction',
    description: 'Limit access to specific locations',
    fields: {
      allowedLocations: { type: 'multiselect', label: 'Allowed Locations' },
      allowedIPs: { type: 'list', label: 'Allowed IP Addresses' },
    },
  },
  
  temporaryAccess: {
    name: 'Temporary Access',
    description: 'Grant access for a limited time',
    fields: {
      startDate: { type: 'datetime', label: 'Start Date' },
      endDate: { type: 'datetime', label: 'End Date' },
      reason: { type: 'text', label: 'Reason' },
    },
  },
  
  approvalRequired: {
    name: 'Approval Required',
    description: 'Require approval for each action',
    fields: {
      approvers: { type: 'userlist', label: 'Approvers' },
      approvalLevel: { type: 'select', label: 'Approval Level', options: ['any', 'all'] },
    },
  },
};

// Helper function to get all permissions for a module
export const getModulePermissions = (moduleName) => {
  const module = PERMISSION_MODULES[moduleName];
  if (!module) return [];
  
  const permissions = [];
  Object.entries(module.resources).forEach(([resourceKey, resource]) => {
    resource.actions.forEach(action => {
      permissions.push({
        key: `${moduleName}.${resourceKey}.${action}`,
        module: moduleName,
        resource: resourceKey,
        action: action,
        name: `${resource.name} - ${action}`,
        scopes: resource.scopes,
        fields: resource.fields,
      });
    });
  });
  
  return permissions;
};

// Helper function to get all permissions in the system
export const getAllPermissions = () => {
  const allPermissions = [];
  Object.keys(PERMISSION_MODULES).forEach(moduleName => {
    allPermissions.push(...getModulePermissions(moduleName));
  });
  return allPermissions;
};

// Export everything for use in components
export default {
  ACTIONS,
  SCOPES,
  FIELD_LEVELS,
  PERMISSION_MODULES,
  PERMISSION_GROUPS,
  PERMISSION_CONDITIONS,
  getModulePermissions,
  getAllPermissions,
};